import { FC, Suspense, useEffect, useRef, useState } from 'react';
import {
  CatTable,
  CatTableBody,
  CatTableHeader,
  CatTableObject,
  CatTableRow
} from 'components/blocks-components';
import ProductQuickViewDrawer from 'components/product-quick-view-drawer';
import { itemSummaryApiCall } from 'services/parts-diagram/actions';
import { PartsTableTitles } from './parts-table-titles';
import { useImpressionReadyEvent } from 'hooks/analytics/use-impression-ready-event';
import PartsTableBody from './parts-table-body';
import { PARTS_DIAGRAM_PAGE } from 'constants/analyticsConstants';
import { useProductClickEvent } from 'hooks/analytics/use-product-click-event';
import styles from './parts-table.module.scss';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { LineItem, PartDetailsAndSchematicResponse } from 'types/parts-diagram';

export interface PartsTableProps {
  partDetailsAndSchematicsData: PartDetailsAndSchematicResponse;
  handlePartView: (lineItem: LineItem) => void;
  shouldHideBtn?: boolean;
  fromProductPage?: boolean;
}

const PartsTable: FC<PartsTableProps> = ({
  partDetailsAndSchematicsData,
  handlePartView,
  shouldHideBtn,
  fromProductPage
}) => {
  const [itemSummaryData, setItemSummaryData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [drawerActive, setDrawerActive] = useState(false);
  const {
    isCatCorp,
    analyticsCommonInfo: { dealerData, model } = {},
    clickedLineItem
  } = usePartDiagram();
  const { fireEvent: fireEventImpressionReady } = useImpressionReadyEvent();
  const { trackEvent: fireEventProductClick } = useProductClickEvent();
  const { dealerName, id: dealerId } = dealerData || {};

  const handleOpen = async (partNumber: string, price) => {
    !isCatCorp && setPriceData(price);
    const itemSummaryResponse = await itemSummaryApiCall(partNumber);
    setItemSummaryData(itemSummaryResponse);
  };

  const { currency = '', lineItems = [] } = partDetailsAndSchematicsData || {};
  const impressionFired = useRef(null);

  useEffect(() => {
    if (!impressionFired.current) {
      fireEventImpressionReady({
        dealerId,
        currencyCode: currency,
        products: lineItems,
        model,
        storeName: dealerName,
        list: PARTS_DIAGRAM_PAGE
      });
      impressionFired.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  useEffect(() => {
    if (clickedLineItem) {
      fireEventProductClick({
        currencyCode: currency,
        dealerId,
        products: [clickedLineItem],
        list: PARTS_DIAGRAM_PAGE,
        storeName: dealerName,
        model
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedLineItem]);

  return (
    <>
      <CatTableObject height="auto" className={styles['parts_table_objects']}>
        <CatTable
          aria-label="parts_Table"
          className={styles['parts-table']}
          /* @ts-expect-error Modifying a var from blocks to fix the default high z-index of the table header cell */
          style={{ '--cat-table-header-cell-zindex-pass-down': '10' }}
        >
          <CatTableHeader>
            <CatTableRow>
              <PartsTableTitles />
            </CatTableRow>
          </CatTableHeader>
          <CatTableBody className={styles['parts-table__body']}>
            {partDetailsAndSchematicsData?.lineItems?.map((lineItem, index) => {
              const {
                partNumber = '',
                ieSystemControlNumber = '',
                name = ''
              } = lineItem || ({} as LineItem);
              return (
                name && (
                  <PartsTableBody
                    key={`${partNumber}-${ieSystemControlNumber}`}
                    lineItem={lineItem}
                    index={index}
                    currency={currency}
                    handlePartView={() => handlePartView(lineItem)}
                    handleOpen={handleOpen}
                    setDrawerActive={setDrawerActive}
                    shouldHideBtn={shouldHideBtn}
                    fromProductPage={fromProductPage}
                  />
                )
              );
            })}
          </CatTableBody>
        </CatTable>
      </CatTableObject>
      {itemSummaryData && (
        <Suspense fallback={null}>
          <ProductQuickViewDrawer
            data={itemSummaryData}
            priceData={priceData}
            dealerData={dealerData}
            currency={currency}
            model={model}
            listEvent={PARTS_DIAGRAM_PAGE}
            drawerActive={drawerActive}
            setDrawerActive={setDrawerActive}
          />
        </Suspense>
      )}
    </>
  );
};

export default PartsTable;
